/* ==========================================================================
   MaxiMeal — Delivery Frequency enhancement
   --------------------------------------------------------------------------
   Turns the native "Delivery Frequency" variant picker (a flat row of equal
   pills: One-time / 1 Month / 2 Months / 3 Months) into a clearer two-tier UI:

       DELIVERY FREQUENCY
       [ (o) One-time                 R450.00 ]
       [ ( ) Subscribe        R382.50  SAVE 15% ]
              Choose delivery schedule
              [ 1 Month ] [ 2 Months ] [ 3 Months ]

   Design notes
   - The real <input type="radio"> nodes are kept and simply re-parented, so
     variant selection, price updates and add-to-cart stay 100% native.
   - "Subscribe" is synthetic (there is no single subscribe radio): clicking it
     selects the currently-chosen month, or the first month by default, and
     reveals the schedule. Selecting One-time hides the schedule again.
   - The product form re-renders itself on every variant change (Shopify Section
     Rendering API), which wipes our rebuilt DOM. A document-level MutationObserver
     re-applies the enhancement after each re-render, and re-syncs active states.

   Contexts — this runs for BOTH:
   - the PDP (picker inside #main-product-anchor), and
   - the quick-view modal (picker inside .quick-buy-modal__content--maxtemp).
   The page can contain more than one picker at once (e.g. a quick view opened
   from a PDP), so every matching picker is enhanced independently, and prices
   are read from the #mm-df-data JSON inside each picker's OWN container.

   No external dependencies.
   ========================================================================== */

(function () {
  'use strict';

  var CONTAINER_SEL = '#main-product-anchor, .quick-buy-modal__content--maxtemp';

  /* ----- Defaults (labels overridden by #mm-df-data) ---------------------- */
  var DEFAULTS = {
    optionName: 'Frequency',   // matched case-insensitively against the rendered legend
    labels: {
      oneTime: 'One-time',
      subscribe: 'Subscribe',
      schedule: 'Choose delivery schedule',
      badge: 'SAVE {n}%'
    },
    showBadge: true
  };
  var ONE_TIME_RE = /one[\s-]?time/i;

  /* ----- Global config (labels are theme settings — identical everywhere) -- */
  function readConfig() {
    var cfg = JSON.parse(JSON.stringify(DEFAULTS));
    var node = document.getElementById('mm-df-data');
    if (node) {
      try {
        var data = JSON.parse(node.textContent);
        if (data.optionName) cfg.optionName = data.optionName;
        if (data.labels) for (var k in data.labels) { if (data.labels[k]) cfg.labels[k] = data.labels[k]; }
        if (typeof data.showBadge === 'boolean') cfg.showBadge = data.showBadge;
      } catch (e) { /* fall back to defaults */ }
    }
    return cfg;
  }

  /* ----- Prices are per-container (each product renders its own #mm-df-data) */
  var priceCache = new WeakMap();
  function pricesFor(container) {
    if (priceCache.has(container)) return priceCache.get(container);
    var promise;
    var node = container.querySelector ? container.querySelector('#mm-df-data') : null;
    var data = null;
    if (node) { try { data = JSON.parse(node.textContent); } catch (e) {} }
    if (data && data.prices) {
      promise = Promise.resolve(data.prices);
    } else {
      // Fallback: fetch the current URL's product (only correct on the PDP).
      var handle = (location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
      promise = handle
        ? fetch('/products/' + handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
            var m = {};
            (p.variants || []).forEach(function (v) {
              var cents = v.price;
              m[String(v.option1 || v.title).trim().toLowerCase()] = {
                cents: cents,
                text: 'R' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              };
            });
            return m;
          }).catch(function () { return {}; })
        : Promise.resolve({});
    }
    priceCache.set(container, promise);
    return promise;
  }

  /* ----- Helpers ---------------------------------------------------------- */
  function cssEscape(id) { return (window.CSS && CSS.escape) ? CSS.escape(id) : id; }
  function containerOf(fs) { return fs.closest(CONTAINER_SEL) || document.body; }

  function findFieldsets(cfg) {
    var re = new RegExp(cfg.optionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    var out = [];
    document.querySelectorAll('.variant-picker__option-name').forEach(function (legend) {
      if (re.test(legend.textContent)) {
        var fs = legend.closest('fieldset');
        if (fs) out.push(fs);
      }
    });
    return out;
  }

  var observer;
  function reveal(fs) { fs.classList.remove('mm-df-pending'); }

  /* ----- Build the two-tier layout for a single picker -------------------- */
  function enhance(cfg, fs) {
    fs.classList.add('mm-df-fieldset');
    if (fs.querySelector('.mm-df')) { reveal(fs); sync(fs); return; }

    var wrap = fs.querySelector('.variant-picker__option-values');
    if (!wrap) { reveal(fs); return; }
    var inputs = Array.prototype.slice.call(wrap.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) { reveal(fs); return; }

    var labelFor = function (inp) { return fs.querySelector('label[for="' + cssEscape(inp.id) + '"]'); };
    var isOneTime = function (inp) { var l = labelFor(inp); return l && ONE_TIME_RE.test(l.textContent); };

    if (!inputs.filter(isOneTime)[0] || !inputs.filter(function (i) { return !isOneTime(i); }).length) {
      reveal(fs); return; // not the expected One-time + subscriptions shape
    }

    pricesFor(containerOf(fs)).then(function (prices) {
      // Re-validate: a re-render may have replaced the fieldset while prices loaded.
      if (!fs.isConnected || fs.querySelector('.mm-df')) { reveal(fs); sync(fs); return; }
      wrap = fs.querySelector('.variant-picker__option-values');
      if (!wrap) { reveal(fs); return; }
      inputs = Array.prototype.slice.call(wrap.querySelectorAll('input[type="radio"]'));
      var oneInput = inputs.filter(isOneTime)[0];
      var subInputs = inputs.filter(function (i) { return !isOneTime(i); });
      if (!oneInput || !subInputs.length) { reveal(fs); return; }

      var priceOf = function (inp) {
        var l = labelFor(inp); return l ? prices[l.textContent.trim().toLowerCase()] : null;
      };
      var oneP = priceOf(oneInput), subP = priceOf(subInputs[0]);
      var oneCents = oneP && oneP.cents, subCents = subP && subP.cents;
      var pct = (oneCents && subCents && oneCents > subCents)
        ? Math.round((oneCents - subCents) / oneCents * 100) : null;

      // Pause the observer so our own writes don't trigger a re-entrant pass.
      if (observer) observer.disconnect();

      var root = document.createElement('div');
      root.className = 'mm-df';

      // One-time bar = the real one-time <label>, restyled (native toggle preserved).
      var oneLabel = labelFor(oneInput);
      oneLabel.classList.remove('block-swatch');
      oneLabel.classList.add('mm-df__bar', 'mm-df__bar--onetime');
      oneLabel.innerHTML =
        '<span class="mm-df__dot" aria-hidden="true"></span>' +
        '<span class="mm-df__title">' + esc(cfg.labels.oneTime) + '</span>' +
        '<span class="mm-df__price">' + (oneP ? esc(oneP.text) : '') + '</span>';
      root.appendChild(oneInput);
      root.appendChild(oneLabel);

      // Subscribe panel: synthetic header + the month radios as the schedule.
      var panel = document.createElement('div');
      panel.className = 'mm-df__panel';

      var head = document.createElement('button');
      head.type = 'button';
      head.className = 'mm-df__bar mm-df__bar--subscribe';
      head.setAttribute('aria-pressed', 'false');
      head.innerHTML =
        '<span class="mm-df__dot" aria-hidden="true"></span>' +
        '<span class="mm-df__title">' + esc(cfg.labels.subscribe) + '</span>' +
        '<span class="mm-df__price">' + (subP ? esc(subP.text) : '') + '</span>' +
        (cfg.showBadge && pct != null
          ? '<span class="mm-df__badge">' + esc(cfg.labels.badge.replace('{n}', pct)) + '</span>'
          : '');
      panel.appendChild(head);

      var sched = document.createElement('div');
      sched.className = 'mm-df__schedule';
      sched.hidden = true;
      var heading = document.createElement('p');
      heading.className = 'mm-df__schedule-heading';
      heading.textContent = cfg.labels.schedule;
      sched.appendChild(heading);

      var opts = document.createElement('div');
      opts.className = 'mm-df__schedule-options';
      subInputs.forEach(function (inp) {
        var l = labelFor(inp);
        l.classList.remove('block-swatch');
        l.classList.add('mm-df__sched-opt');
        opts.appendChild(inp);
        opts.appendChild(l);
      });
      sched.appendChild(opts);
      panel.appendChild(sched);
      root.appendChild(panel);

      wrap.replaceWith(root);
      reveal(fs); // upgrade is in place — drop the anti-flash hide

      var legend = fs.querySelector('.variant-picker__option-name');
      if (legend) legend.textContent = (legend.textContent || '').toUpperCase();

      // Subscribe header → select the active (or first) month, reveal the schedule.
      head.addEventListener('click', function () {
        var target = subInputs.filter(function (i) { return i.checked; })[0] || subInputs[0];
        if (!target.checked) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        sync(fs);
      });
      // Any native change (month pill, deep-link, back button) re-syncs the UI.
      fs.addEventListener('change', function () { sync(fs); });

      sync(fs);
      observe(); // re-arm
    });
  }

  /* ----- Reflect the checked radio in one picker -------------------------- */
  function sync(fs) {
    var oneBar = fs.querySelector('.mm-df__bar--onetime');
    var head = fs.querySelector('.mm-df__bar--subscribe');
    var sched = fs.querySelector('.mm-df__schedule');
    if (!oneBar || !head || !sched) return;

    var oneRadio = fs.querySelector('.mm-df > input[type="radio"]');
    var subActive = false;
    fs.querySelectorAll('.mm-df__schedule-options input[type="radio"]').forEach(function (i) {
      if (i.checked) subActive = true;
    });

    oneBar.classList.toggle('is-active', !!(oneRadio && oneRadio.checked));
    head.classList.toggle('is-active', subActive);
    head.setAttribute('aria-pressed', subActive ? 'true' : 'false');
    sched.hidden = !subActive;
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }

  /* ----- Process every picker on the page --------------------------------- */
  function run(cfg) {
    findFieldsets(cfg).forEach(function (fs) {
      if (fs.querySelector('.mm-df')) sync(fs);
      else enhance(cfg, fs);
    });
  }

  /* ----- Boot + survive re-renders ---------------------------------------- */
  function init() {
    var cfg = readConfig();
    var pending = null;
    observer = new MutationObserver(function () {
      // Hide any freshly-rendered native pills synchronously (this callback runs
      // before paint) so they never flash, then debounce the actual rebuild.
      findFieldsets(cfg).forEach(function (fs) {
        if (!fs.querySelector('.mm-df')) {
          fs.classList.add('mm-df-fieldset');
          fs.classList.add('mm-df-pending');
        }
      });
      if (pending) return;
      pending = setTimeout(function () { pending = null; run(cfg); }, 60);
    });
    run(cfg);
    observe();
  }

  function observe() {
    // Observe a stable ancestor: the theme replaces #main-product-anchor itself
    // on variant change, and the quick view is injected into the modal, so
    // watching body is what survives both.
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
